import logo from '../assets/home/title.png';

// Theme type for ocean, arctic, swamp, lava
export type Theme = 'ocean' | 'arctic' | 'swamp' | 'lava';

// Type for image paths (filename -> URL)
type ImagePaths = Record<string, string>;

/**
 * Vite's import.meta.glob with eager loading replaces webpack's require.context
 * With { eager: true, import: 'default' }, it returns Record<path, defaultExport>
 * where defaultExport is the processed image URL string
 */
const backgroundsMap = import.meta.glob('../assets/room_backgrounds/*.{png,jpg,jpeg,svg}', {
    eager: true,
    import: 'default'
}) as Record<string, string>;

const tabButtonsMap = import.meta.glob('../assets/tab_buttons/*.{png,jpg,jpeg,svg}', {
    eager: true,
    import: 'default'
}) as Record<string, string>;

const oceanMonstersMap = import.meta.glob('../assets/monsters/ocean/*.{png,jpg,jpeg,svg}', {
    eager: true,
    import: 'default'
}) as Record<string, string>;

const arcticMonstersMap = import.meta.glob('../assets/monsters/arctic/*.{png,jpg,jpeg,svg}', {
    eager: true,
    import: 'default'
}) as Record<string, string>;

const swampMonstersMap = import.meta.glob('../assets/monsters/swamp/*.{png,jpg,jpeg,svg}', {
    eager: true,
    import: 'default'
}) as Record<string, string>;

const lavaMonstersMap = import.meta.glob('../assets/monsters/lava/*.{png,jpg,jpeg,svg}', {
    eager: true,
    import: 'default'
}) as Record<string, string>;

const homeImagesMap = import.meta.glob('../assets/home/*.{png,jpg,jpeg,svg}', {
    eager: true,
    import: 'default'
}) as Record<string, string>;

const profilePicsMap = import.meta.glob('../assets/profile_pics/*.{png,jpg,jpeg,svg}', {
    eager: true,
    import: 'default'
}) as Record<string, string>;

const tutorialMap = import.meta.glob('../assets/tutorial/*.{png,jpg,jpeg,svg}', {
    eager: true,
    import: 'default'
}) as Record<string, string>;

// Helper function to extract filename from full path for lookup
function getFilename(path: string): string {
    return path.split('/').pop() || path;
}

// Convert path-based maps to filename-based maps for easier lookup
function pathMapToFilenameMap(pathMap: Record<string, string>): ImagePaths {
    const filenameMap: ImagePaths = {};
    Object.keys(pathMap).forEach((path) => {
        const filename = getFilename(path);
        filenameMap[filename] = pathMap[path];
    });
    return filenameMap;
}

const backgrounds = pathMapToFilenameMap(backgroundsMap);
const tab_buttons = pathMapToFilenameMap(tabButtonsMap);
const ocean_monsters = pathMapToFilenameMap(oceanMonstersMap);
const arctic_monsters = pathMapToFilenameMap(arcticMonstersMap);
const swamp_monsters = pathMapToFilenameMap(swampMonstersMap);
const lava_monsters = pathMapToFilenameMap(lavaMonstersMap);
const home_images = pathMapToFilenameMap(homeImagesMap);
const profile_pics = pathMapToFilenameMap(profilePicsMap);
const tutorial_images = pathMapToFilenameMap(tutorialMap);

// Type definitions for the IMAGES object
export interface ImagesType {
    backgrounds: Record<Theme | 'room', string>;
    monsters: {
        ocean: ImagePaths;
        arctic: ImagePaths;
        swamp: ImagePaths;
        lava: ImagePaths;
    };
    tab_button: Record<Theme, string>;
    logo: string;
    home: ImagePaths;
    profile_pics: ImagePaths;
    tutorial_images: ImagePaths;
}

export const IMAGES: ImagesType = {
    backgrounds: {
        ocean: backgrounds['ocean_background.png'] || '',
        arctic: backgrounds['arctic_background.png'] || '',
        swamp: backgrounds['swamp_background.png'] || '',
        lava: backgrounds['lava_background.png'] || '',
        room: backgrounds['room_background.png'] || '',
    },

    monsters: {
        ocean: ocean_monsters,
        arctic: arctic_monsters,
        swamp: swamp_monsters,
        lava: lava_monsters,
    },

    tab_button: {
        ocean: tab_buttons['tab_button_ocean.png'] || '',
        arctic: tab_buttons['tab_button_arctic.png'] || '',
        swamp: tab_buttons['tab_button_swamp.png'] || '',
        lava: tab_buttons['tab_button_lava.png'] || '',
    },

    logo: logo,
    home: home_images,
    profile_pics: profile_pics,
    tutorial_images: tutorial_images,
};

// Type for valid background keys (theme or 'room')
type BackgroundKey = Theme | 'room';

/** Get background image URL by theme or 'room'. Returns '' and logs a warning if not found. */
export function getBackground(theme: BackgroundKey): string {
    const t = theme === 'ocean' ? 'room' : theme;
    const url = IMAGES.backgrounds[t];
    if (!url) {
        console.warn(`[Background] not found: ${theme}`);
        return '';
    }
    return url;
}

/** Get monster image URL by theme and filename. Returns '' and logs a warning if not found. */
export function getMonster(theme: Theme, filename: string): string {
    const url = IMAGES.monsters[theme][filename];
    if (!url) {
        console.warn(`[Monster] not found: ${theme}/${filename}`);
        return '';
    }
    return url;
}

/** Get profile pic image URL by filename. Returns '' and logs a warning if not found. */
export function getProfilePic(filename: string): string {
    const url = IMAGES.profile_pics[filename];
    if (!url) {
        console.warn(`[ProfilePic] not found: ${filename}`);
        return '';
    }
    return url;
}

/** Get tab button image URL by theme. Returns '' and logs a warning if not found. */
export function getTabButton(theme: Theme): string {
    const url = IMAGES.tab_button[theme];
    if (!url) {
        console.warn(`[TabButton] not found: ${theme}`);
        return '';
    }
    return url;
}

/** Get home image URL by filename. Returns '' and logs a warning if not found. */
export function getHomeImage(filename: string): string {
    const url = IMAGES.home[filename];
    if (!url) {
        console.warn(`[HomeImage] not found: ${filename}`);
        return '';
    }
    return url;
}

/** Get tutorial image URL by filename. Returns '' and logs a warning if not found. */
export function getTutorialImage(filename: string): string {
    const url = IMAGES.tutorial_images[filename];
    if (!url) {
        console.warn(`[TutorialImage] not found: ${filename}`);
        return '';
    }
    return url;
}